import React, { ReactElement, useState } from "react";
import styles from "./HomePage.module.scss";
import { useQuery } from "@tanstack/react-query";
import { QueryKey, TransactionType } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import { Block, TxData } from "../../models/Block";
import { Anchor, Box, Group, Loader, Stack, Switch, Text } from "@mantine/core";
import { formatAddress, formatNumber } from "../../utils/formatter";
import PagingButtons from "../../components/pagingButtons/PagingButtons.component";
import { FETCH_NUMBER_OF_BLOCKS } from "../../constants";
import { formatDate, useAvailableTime } from "../../utils/formatTime";
import { getTransactionTypeLabel } from "../transactionDetails/TransactionConfig";
import { useStore } from "@tanstack/react-store";
import { IStore, store } from "../../services/store.service";
import { Filter } from "../../components/Filter/Filter.component";
import usePageParam from "../../hooks/usePageParam";

export default function HomePage(): ReactElement | null {
  const timeZone = useStore<IStore, IStore["timeZone"]>(store, (state) => state.timeZone).selected;
  const [showEmptyBlocks, setShowEmptyBlocks] = useState(false);
  const [txType, setTxType] = useState<TransactionType | null>(null)

  const {page, setPage} = usePageParam();

  const formatBlockType = (type: string): string => {
    const words = type.split("_");
    return words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
  };

  const TNX_TYPES_WEIGHT: string[] = [
    TransactionType.ADD_DELEGATOR_TX,
    TransactionType.ADD_VALIDATOR_TX,
    TransactionType.ADVANCE_TIME_TX,
    TransactionType.REWARD_TX,
    TransactionType.ADD_PERMISSIONLESS_DELEGATOR_TX,
    TransactionType.ADD_PERMISSIONLESS_VALIDATOR_TX,
  ];

  const getAmount = (tx: TxData): number => {
    if (TNX_TYPES_WEIGHT.includes(tx.type)) {
      return tx.weight;
    } else if (tx.type === TransactionType.IMPORT_TX) {
      return tx.outputAmount;
    } else if (tx.type === TransactionType.EXPORT_TX || tx.type === TransactionType.BASE_TX) {
      return tx.weight;
    } else {
      console.warn("Unknown transaction type: ", tx.type);
      return 0;
    }
  };

  const getType = (tx: TxData): string => {
    if (tx.type === TransactionType.ADD_PERMISSIONLESS_DELEGATOR_TX) {
      return "Add delegator";
    } else if (tx.type === TransactionType.ADD_PERMISSIONLESS_VALIDATOR_TX) {
      return "Add validator";
    } else return getTransactionTypeLabel(tx.type as TransactionType);
  };

  const { data: blockHeight } = useQuery({
    queryKey: [QueryKey.blockHeight, showEmptyBlocks],
    queryFn: async (): Promise<number> => IndexerService.getLastBlock(),
    refetchInterval: page === 1 ? 5000 : false,
    enabled: true, // fetches on load
  });

  const { data: blocks, isLoading: blocksLoading } = useQuery({
    queryKey: [QueryKey.blocks, page, blockHeight, showEmptyBlocks, txType],
    queryFn: async (): Promise<Block[] | null> => {
      if (page === 1) {
        return IndexerService.getLastBlocks(
          FETCH_NUMBER_OF_BLOCKS,
          showEmptyBlocks,
          txType?.toString(),
        );
      }
      return IndexerService.getBlocksPerPage(
        page,
        FETCH_NUMBER_OF_BLOCKS,
        showEmptyBlocks,
        txType?.toString(),
      );
    },
    enabled: !!blockHeight,
  });

  const blockItems: JSX.Element[] =
    blocks
      ?.filter(
        (block: Block) => showEmptyBlocks || block.transactions.length > 0,
      )
      ?.map((block: Block) => (
        <Box className={styles.txItem} key={block.blockID}>
          <Group
            className={styles.headingValueContainerGroup}
            justify="flex-start"
            wrap={"nowrap"}
          >
            <Box className={styles.headingValueContainer}>
              <span className={styles.txLabelHeading}>Block</span>
              <Box display="flex" className={styles.blockInfoWrapper}>
                <Text mr="2" className={styles.txLabelValue}>
                  {block.height}
                </Text>
                <Text
                  mr="2"
                  fw={500}
                  className={`${styles.txLabelValue} ${styles.verticalLine}`}
                >
                  |
                </Text>
                <Text fw={500} className={styles.txLabelValue}>
                  {formatBlockType(block.type)}
                </Text>
              </Box>
            </Box>

            <Stack
              gap={0}
              ta={"start"}
              w={"fit-content"}
              className={styles.rowsWrapper}
            >
              {/*No transactions in block */}
              {block.transactions.length === 0 ? (
                <Box className={styles.emptyItem}>
                  <Box className={styles.headingValueContainer}>
                    <span className={styles.txLabelHeading}>Transaction</span>
                    <span className={styles.txLabelValue}>
                      No transactions in this block.
                    </span>
                  </Box>
                </Box>
              ) : (
                block.transactions.map((tx: TxData, index: number) => (
                  <Box key={index}>
                    <Box className={styles.wrappingBlock}>
                      <Group
                        className={styles.itemContainer}
                        key={tx.txID}
                        style={{ width: "100%" }}
                      >
                        <Box className={styles.txColumn}>
                          <span className={styles.txLabelHeading}>
                            Transaction
                          </span>
                          <Anchor
                            className={[styles.txLabelValue, styles.link].join(
                              " ",
                            )}
                            href={`${process.env.PUBLIC_URL}/tx/${tx.txID}`}
                            c="var(--quinary)"
                          >
                            {formatAddress(tx.txID)}
                          </Anchor>
                        </Box>
                        <Box className={styles.typeColumn}>
                          <span className={styles.txLabelHeading}>Type</span>
                          <span className={styles.txLabelValue}>
                          {getType(tx)}
                          </span>
                        </Box>
                        <Box className={styles.amountColumn}>
                          <span className={styles.txLabelHeading}>Amount</span>
                          <Box display={"flex"}>
                            <span className={styles.txLabelValue}>
                              {formatNumber(getAmount(tx))}
                            </span>
                            <Text
                              fw={400}
                              className={styles.txLabelValue}
                              ml="5"
                            >
                              {" "}
                              FLR
                            </Text>
                          </Box>
                        </Box>
                        <Box className={styles.timestampColumn}>
                          <Box className={styles.timestampWrapper}>
                            <span className={styles.txLabelHeading}>
                              Timestamp
                            </span>
                            {!useAvailableTime(tx) ? (
                              <span> No date provided</span>
                            ) : (
                              <Box className={styles.dateWrapper}>
                                <span className={styles.txLabelValue}>
                                  {
                                    formatDate(new Date(useAvailableTime(tx)!), timeZone)
                                      .formattedDateTime
                                  }
                                </span>
                                &nbsp;
                                <span className={styles.unitLabel}>
                                  {formatDate(new Date(useAvailableTime(tx)!), timeZone).utcOffset}
                                </span>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Group>
                    </Box>
                    {index < block.transactions.length - 1 && <hr />}
                  </Box>
                ))
              )}
            </Stack>
          </Group>
        </Box>
      )) || [];

  return (
    <>
      <div className={styles.homeContent}>
        <div className={styles.latestBlocks}>
          <h2>Latest blocks {process.env.REACT_APP_CHAIN && process.env.REACT_APP_CHAIN !== "flr"
            ? ` - ${process.env.REACT_APP_CHAIN.toUpperCase()}`
            : ""}</h2>
          <div>
          </div>
          <Filter setPage={setPage} txType={txType} setTxType={setTxType} showEmptyBlocks={showEmptyBlocks} setShowEmptyBlocks={setShowEmptyBlocks}
           />
        </div>

        {blocksLoading ? (
          <Loader size={50} style={{ marginTop: "20%" }} />
        ) : (
          <>
            <div className={styles.lastBlocksContainer}>{blockItems}</div>
            <Box right="right">
              <PagingButtons
                page={page}
                setPage={setPage}
                blockHeight={blockHeight ?? 0}
              />
            </Box>
          </>
        )}
      </div>
    </>
  );
}

